import React from "react";
import {
  BenefitItemProps,
  Plan,
  SupplementalBenefitCategoryTypeNames,
} from "../../../@types";
import { CollapsibleBox, LimitsApplyAccordion } from "../../../components";
import {
  getBenefitCost,
  getBenefitExclusionsAndLimitations,
  makeBenefitItemItems,
} from "../../../helpers";
import { Ga4Event } from "../../../helpers/analytics";
import {
  getMMPBenefitsCoverageKey,
  getReferralAndAuthorizationRequired,
  OtherServicesBenefitDetails,
} from "./helpers";

export const dataAccessor = {
  getDoctorServicesData: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string
  ): Array<{
    key: string | JSX.Element;
    value: string;
    limitsApply: JSX.Element;
  }> =>
    arrangeBenefitItems(
      [
        {
          label: t("plan_terms.primary_doctor_visit"),
          service: "primary",
        },
        {
          label: t("plan_terms.specialist_visit"),
          service: "specialist",
        },
      ],
      "doctor_visits",
      plan
    ).map(({ label, ...rest }) => ({
      key: label,
      value: getBenefitCost(t, { ...rest }),
      limitsApply: (
        <LimitsApplyAccordion
          descriptors={getBenefitExclusionsAndLimitations({
            ...rest,
          })}
          ga4SectionName={`${label}`}
          tealiumSectionName={`${categoryLabel}|${label}`}
        />
      ),
    })),
  getTestsLabsAndImaging: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string,
    previewMode: boolean
  ): Array<{
    key: string | JSX.Element;
    value: string;
    limitsApply: JSX.Element;
  }> => {
    const testsAndProceduresLabel = t(
      "plan_terms.diagnostic_tests_and_procedures"
    );
    return [
      ...arrangeBenefitItems(
        [
          {
            label: (
              <CollapsibleBox
                labelId="plan_terms.diagnostic_tests_and_procedures"
                titleClassname="diagnostic_tests_and_procedures__heading"
                tealiumProps={{
                  event_action: "Plan Details Help Caret",
                  event_label: `${categoryLabel}|${testsAndProceduresLabel}`,
                }}
                ga4Props={{
                  event_name: Ga4Event.PLAN_DETAILS_HELP_CARET_CLICKED,
                  header: testsAndProceduresLabel,
                }}
                open={previewMode}
              >
                {t("plan_terms.diagnostic_tests_and_procedures.definition")}
              </CollapsibleBox>
            ),
            service: "diagnostic_tests",
          },
          {
            label: t("plan_terms.lab_services"),
            service: "lab_services",
          },
          {
            label: t("plan_terms.diagnostic_radiology_services"),
            service: "diagnostic_radiology_services",
          },
          {
            label: t("plan_terms.outpatient_xrays"),
            service: "outpatient_xrays",
          },
        ],
        "diagnostic_procedures",
        plan
      ),
      ...arrangeBenefitItems(
        [
          {
            label: t("plan_terms.emergency_care"),
            service: "emergency",
          },
          {
            label: t("plan_terms.urgent_care"),
            service: "urgent_care",
          },
        ],
        "emergency_care",
        plan
      ),
    ].map(({ label, ...rest }, index) => {
      const sectionName =
        index === 0 ? testsAndProceduresLabel : label.toString();
      return {
        key: label,
        value: getBenefitCost(t, { ...rest }),
        limitsApply: (
          <LimitsApplyAccordion
            descriptors={getBenefitExclusionsAndLimitations({
              ...rest,
            })}
            ga4SectionName={sectionName}
            tealiumSectionName={`${categoryLabel}|${sectionName}`}
          />
        ),
      };
    });
  },
  getHospitalServices: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string
  ): Array<{
    key: string | JSX.Element;
    value: string;
    limitsApply: JSX.Element;
  }> =>
    [
      ...arrangeBenefitItems(
        [
          {
            label: t("plan_terms.inpatient_hospital_coverage"),
          },
        ],
        "inpatient_hospital",
        plan
      ),
      ...arrangeBenefitItems(
        [
          {
            label: t("plan_terms.outpatient_hospital_coverage"),
          },
        ],
        "outpatient_hospital",
        plan
      ),
    ].map(({ label, ...rest }) => ({
      key: label,
      value: getBenefitCost(t, { ...rest }),
      limitsApply: (
        <LimitsApplyAccordion
          descriptors={getBenefitExclusionsAndLimitations({
            ...rest,
          })}
          ga4SectionName={`${label}`}
          tealiumSectionName={`${categoryLabel}|${label}`}
        />
      ),
    })),
  getSkilledNursingFacility: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string
  ): Array<{
    key: string | JSX.Element;
    value: string;
    limitsApply: JSX.Element;
  }> =>
    [
      ...arrangeBenefitItems(
        [
          {
            label: t("plan_terms.skilled_nursing"),
          },
        ],
        "skilled_nursing_facility",
        plan
      ),
    ].map(({ label, ...rest }) => ({
      key: label,
      value: getBenefitCost(t, { ...rest }),
      limitsApply: (
        <LimitsApplyAccordion
          descriptors={getBenefitExclusionsAndLimitations({
            ...rest,
          })}
          ga4SectionName={`${label}`}
          tealiumSectionName={`${categoryLabel}|${label}`}
        />
      ),
    })),
  getPreventiveServices: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string
  ): Array<{
    key: string | JSX.Element;
    value: string;
    limitsApply: JSX.Element;
  }> =>
    [
      ...arrangeBenefitItems(
        [
          {
            label: t("plan_terms.preventive_services"),
          },
        ],
        "preventive_care",
        plan
      ),
    ].map(({ label, ...rest }) => ({
      key: label,
      value: getBenefitCost(t, { ...rest }),
      limitsApply: (
        <LimitsApplyAccordion
          descriptors={getBenefitExclusionsAndLimitations({
            ...rest,
          })}
          ga4SectionName={`${label}`}
          tealiumSectionName={`${categoryLabel}|${label}`}
        />
      ),
    })),
  getAmbulance: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string
  ): Array<{
    key: string | JSX.Element;
    value: string;
    limitsApply: JSX.Element;
  }> =>
    arrangeBenefitItems(
      [
        {
          label: t("plan_terms.ambulance_ground"),
        },
      ],
      "ambulance",
      plan
    ).map(({ label, ...rest }) => ({
      key: label,
      value: getBenefitCost(t, { ...rest }),
      limitsApply: (
        <LimitsApplyAccordion
          descriptors={getBenefitExclusionsAndLimitations({
            ...rest,
          })}
          ga4SectionName={`${label}`}
          tealiumSectionName={`${categoryLabel}|${label}`}
        />
      ),
    })),
  getTherapyServices: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string
  ): Array<{
    key: string | JSX.Element;
    value: string;
    limitsApply: JSX.Element;
  }> =>
    arrangeBenefitItems(
      [
        {
          label: t("plan_terms.therapy_services.occupational_therapy"),
          service: "occupational_therapy_visit",
        },
        {
          label: t("plan_terms.therapy_services.physical_and_speech_therapy"),
          service: "physical_therapy_and_speech_and_language_therapy_visit",
        },
      ],
      "rehabilitation_services",
      plan
    ).map(({ label, ...rest }) => ({
      key: label,
      value: getBenefitCost(t, { ...rest }),
      limitsApply: (
        <LimitsApplyAccordion
          descriptors={getBenefitExclusionsAndLimitations({
            ...rest,
          })}
          ga4SectionName={`${label}`}
          tealiumSectionName={`${categoryLabel}|${label}`}
        />
      ),
    })),
  getMentalHealthServices: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string
  ): Array<{
    key: string | JSX.Element;
    value: string;
    limitsApply: JSX.Element;
  }> =>
    makeBenefitItemItems(
      [
        {
          label: t("plan_terms.mental_health.outpatient_group_psy"),
          service: "outpatient_group_therapy_visit_with_psychiatrist",
        },
        {
          label: t("plan_terms.mental_health.outpatient_ind_psy"),
          service: "outpatient_individual_therapy_visit_with_psychiatrist",
        },
        {
          label: t("plan_terms.mental_health.outpatient_group"),
          service: "outpatient_group_therapy_visit",
        },
        {
          label: t("plan_terms.mental_health.outpatient_ind"),
          service: "outpatient_individual_therapy_visit",
        },
      ],
      "mental_health",
      plan
    ).map(({ label, ...rest }) => ({
      key: label,
      value: getBenefitCost(t, { ...rest }),
      limitsApply: (
        <LimitsApplyAccordion
          descriptors={getBenefitExclusionsAndLimitations({
            ...rest,
          })}
          ga4SectionName={`${label}`}
          tealiumSectionName={`${categoryLabel}|${label}`}
        />
      ),
    })),
  // plan_card.additional_supplemental_benefits.other_benefits
  // category: SB_CAT_OPIOID_TREATMENT_SERVICES
  getOpioidTreatmentProgramServices: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string,
    suppressed: boolean
  ): Array<{
    key: string | JSX.Element;
    value: JSX.Element;
    limitsApply: JSX.Element;
  }> => {
    const limitsApplyData = getReferralAndAuthorizationRequired(
      plan,
      SupplementalBenefitCategoryTypeNames.SB_CAT_OPIOID_TREATMENT_SERVICES
    );
    const label = t("more_benefits.opioid_treatment");
    return [
      {
        key: label,
        value: (
          <OtherServicesBenefitDetails
            {...{
              t,
              plan,
              category:
                SupplementalBenefitCategoryTypeNames.SB_CAT_OPIOID_TREATMENT_SERVICES,
              suppressed,
            }}
          />
        ),
        limitsApply: (
          <LimitsApplyAccordion
            descriptors={{
              authorization_required: limitsApplyData.authorizationRequired,
              referral_required: limitsApplyData.referralRequired,
              plan_limits: false,
            }}
            ga4SectionName={`${label}`}
            tealiumSectionName={`${categoryLabel}|${label}`}
          />
        ),
      },
    ];
  },
  getOtherServices: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string,
    suppressed: boolean
  ): Array<{
    key: string | JSX.Element;
    value: string | JSX.Element;
    limitsApply: JSX.Element;
  }> => {
    const otherBenefits = [
      ...arrangeBenefitItems(
        [
          {
            label: t("plan_terms.other_services.durable_equip"),
            service: "durable_medical_equipment",
          },
          {
            label: t("plan_terms.other_services.prosthetics"),
            service: "prosthetics",
          },
          {
            label: t("plan_terms.other_services.diabetes"),
            service: "diabetes_supplies",
          },
        ],
        "medical_equipment",
        plan
      ),
    ].map(({ label, ...rest }) => ({
      key: label,
      value: getBenefitCost(t, { ...rest }),
      limitsApply: (
        <LimitsApplyAccordion
          descriptors={getBenefitExclusionsAndLimitations({
            ...rest,
          })}
          ga4SectionName={`${label}`}
          tealiumSectionName={`${categoryLabel}|${label}`}
        />
      ),
    }));

    const limitsApplyData = getReferralAndAuthorizationRequired(
      plan,
      SupplementalBenefitCategoryTypeNames.SB_CAT_DIALYSIS_SERVICES
    );
    const label = t("plan_terms.other_services.dialysis");
    const dialysisBenefit = {
      key: label,
      value: (
        <OtherServicesBenefitDetails
          {...{
            t,
            plan,
            category:
              SupplementalBenefitCategoryTypeNames.SB_CAT_DIALYSIS_SERVICES,
            suppressed,
          }}
        />
      ),
      limitsApply: (
        <LimitsApplyAccordion
          descriptors={{
            authorization_required: limitsApplyData.authorizationRequired,
            referral_required: limitsApplyData.referralRequired,
            plan_limits: false,
          }}
          ga4SectionName={`${label}`}
          tealiumSectionName={`${categoryLabel}|${label}`}
        />
      ),
    };

    return [
      ...otherBenefits.slice(0, -1),
      dialysisBenefit,
      ...otherBenefits.slice(-1),
    ];
  },
  getMMPBenefits: (
    plan: Plan,
    categoryLabel: string,
    t: (id: string) => string
  ): Array<{
    key: string | JSX.Element;
    value: string;
    limitsApply: JSX.Element;
  }> => {
    const hoursOfCareLabel = t(
      "plan_terms.mmp_home_health_services.additional_hours_of_care"
    );
    const personalCareServicesLabel = t(
      "plan_terms.mmp_home_health_services.personal_care_services"
    );
    return [
      {
        key: hoursOfCareLabel,
        value: t(
          getMMPBenefitsCoverageKey(
            plan,
            "home_health_services",
            "additional_hours_of_care"
          ).toLowerCase()
        ),
        limitsApply: (
          <LimitsApplyAccordion
            descriptors={getBenefitExclusionsAndLimitations({
              plan,
              benefit: "additional_hours_of_care",
            })}
            ga4SectionName={hoursOfCareLabel}
            tealiumSectionName={`${categoryLabel}|${hoursOfCareLabel}`}
          />
        ),
      },
      {
        key: personalCareServicesLabel,
        value: t(
          getMMPBenefitsCoverageKey(
            plan,
            "home_health_services",
            "home_personal_care_services"
          ).toLowerCase()
        ),
        limitsApply: (
          <LimitsApplyAccordion
            descriptors={getBenefitExclusionsAndLimitations({
              plan,
              benefit: "home_personal_care_services",
            })}
            ga4SectionName={personalCareServicesLabel}
            tealiumSectionName={`${categoryLabel}|${personalCareServicesLabel}`}
          />
        ),
      },
    ];
  },
};

const arrangeBenefitItems = (
  items: Array<{ label: string | JSX.Element; service?: string }>,
  benefit: string,
  plan: Plan
): BenefitItemProps[] => makeBenefitItemItems(items, benefit, plan);
